/**
 * Journal d'audit Admin — Lot 12 / 12bis.
 */
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  auditApi,
  ApiRequestError,
  type AuditEntryDto,
} from "../api/api";
import { PageHeader } from "../components/ui/PageHeader";

function formatVal(v: unknown): string {
  if (v === null || v === undefined) return "∅";
  if (typeof v === "string") return v.length > 80 ? `${v.slice(0, 80)}…` : v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  try {
    const s = JSON.stringify(v);
    return s.length > 100 ? `${s.slice(0, 100)}…` : s;
  } catch {
    return String(v);
  }
}

/** Affiche un label de champ plus lisible (ex. BPML_READY.endsOn). */
function formatFieldLabel(key: string): string {
  return key.replace(/\./g, " · ");
}

function DetailsCell({ details }: { details: AuditEntryDto["details"] }) {
  if (!details || typeof details !== "object") return <span className="sm-muted">—</span>;
  const changed =
    "changed" in details && details.changed && typeof details.changed === "object"
      ? (details.changed as Record<string, { from: unknown; to: unknown }>)
      : null;
  const summary =
    "summary" in details && details.summary && typeof details.summary === "object"
      ? (details.summary as Record<string, unknown>)
      : null;

  const lines: string[] = [];
  if (summary) {
    const ctx = [summary.domain, summary.year, summary.step]
      .filter((x) => x !== undefined && x !== null && x !== "")
      .map(String);
    if (ctx.length) lines.push(ctx.join(" / "));
  }
  if (changed) {
    for (const [k, v] of Object.entries(changed)) {
      lines.push(`${formatFieldLabel(k)}: ${formatVal(v.from)} → ${formatVal(v.to)}`);
    }
  } else if (summary) {
    for (const [k, v] of Object.entries(summary)) {
      if (k === "domain" || k === "year" || k === "step") continue;
      lines.push(`${k}=${formatVal(v)}`);
    }
  }

  if (!lines.length) return <span className="sm-muted">—</span>;

  return (
    <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, maxWidth: 420 }}>
      {lines.slice(0, 10).map((line, i) => (
        <li key={i} style={{ wordBreak: "break-word" }}>
          {line}
        </li>
      ))}
      {lines.length > 10 ? (
        <li className="sm-muted">+{lines.length - 10}…</li>
      ) : null}
    </ul>
  );
}

export function AuditLogPage() {
  const { t } = useTranslation();
  const [items, setItems] = useState<AuditEntryDto[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [actionType, setActionType] = useState("");
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [actionTypes, setActionTypes] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await auditApi.list({
        page,
        pageSize,
        actionType: actionType || undefined,
        q: q.trim() || undefined,
        from: from || undefined,
        to: to || undefined,
      });
      setItems(res.items);
      setTotal(res.total);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("audit_load_failed"));
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, actionType, q, from, to, t]);

  useEffect(() => {
    void auditApi
      .actionTypes()
      .then((r) => setActionTypes(r.actionTypes))
      .catch(() => setActionTypes([]));
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="sm-page">
      <PageHeader title={t("nav_audit")} subtitle={t("audit_sub")} />

      <div className="sm-card sm-card-pad" style={{ marginBottom: 12 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
            gap: 12,
            alignItems: "end",
          }}
        >
          <div>
            <label htmlFor="audit-q" className="sm-muted" style={{ fontSize: 12 }}>
              {t("audit_search")}
            </label>
            <input
              id="audit-q"
              className="sm-input"
              value={q}
              onChange={(e) => {
                setPage(1);
                setQ(e.target.value);
              }}
              placeholder={t("audit_search_ph")}
            />
          </div>
          <div>
            <label htmlFor="audit-action" className="sm-muted" style={{ fontSize: 12 }}>
              {t("audit_action")}
            </label>
            <select
              id="audit-action"
              className="sm-input"
              value={actionType}
              onChange={(e) => {
                setPage(1);
                setActionType(e.target.value);
              }}
            >
              <option value="">{t("all")}</option>
              {actionTypes.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="audit-from" className="sm-muted" style={{ fontSize: 12 }}>
              {t("audit_from")}
            </label>
            <input
              id="audit-from"
              type="date"
              className="sm-input"
              value={from}
              onChange={(e) => {
                setPage(1);
                setFrom(e.target.value);
              }}
            />
          </div>
          <div>
            <label htmlFor="audit-to" className="sm-muted" style={{ fontSize: 12 }}>
              {t("audit_to")}
            </label>
            <input
              id="audit-to"
              type="date"
              className="sm-input"
              value={to}
              onChange={(e) => {
                setPage(1);
                setTo(e.target.value);
              }}
            />
          </div>
        </div>
      </div>

      {error ? (
        <div className="sm-alert sm-alert-error" role="alert">
          {error}
        </div>
      ) : null}

      <p className="sm-muted" style={{ fontSize: 13 }}>
        {t("audit_total", { total })}
      </p>

      <div className="sm-card">
        <div className="sm-table-wrap">
          <table className="sm-table">
            <thead>
              <tr>
                <th>{t("audit_when")}</th>
                <th>{t("audit_actor")}</th>
                <th>{t("audit_action")}</th>
                <th>{t("audit_target")}</th>
                <th>{t("reason")}</th>
                <th>{t("audit_details")}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="sm-muted" style={{ textAlign: "center", padding: 24 }}>
                    {t("loading")}
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={6} className="sm-muted" style={{ textAlign: "center", padding: 24 }}>
                    {t("no_data")}
                  </td>
                </tr>
              ) : (
                items.map((row) => (
                  <tr key={row.id}>
                    <td className="sm-muted" style={{ fontSize: 13, whiteSpace: "nowrap" }}>
                      {new Date(row.createdAt).toLocaleString()}
                    </td>
                    <td>
                      {row.actorName ?? "—"}
                      {row.actorEmail ? (
                        <div className="sm-muted" style={{ fontSize: 11 }}>
                          {row.actorEmail}
                        </div>
                      ) : null}
                    </td>
                    <td>
                      <code style={{ fontSize: 12 }}>{row.actionType}</code>
                    </td>
                    <td style={{ fontSize: 13 }}>
                      <span className="sm-muted">{row.targetType}</span>
                      {row.targetId ? (
                        <div style={{ fontFamily: "monospace", fontSize: 11 }}>
                          {row.targetId.length > 36
                            ? `${row.targetId.slice(0, 36)}…`
                            : row.targetId}
                        </div>
                      ) : null}
                    </td>
                    <td className="sm-muted" style={{ fontSize: 12, maxWidth: 200 }}>
                      {row.reason
                        ? row.reason.length > 120
                          ? `${row.reason.slice(0, 120)}…`
                          : row.reason
                        : "—"}
                    </td>
                    <td>
                      <DetailsCell details={row.details} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 ? (
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 12 }}>
          <button
            type="button"
            className="sm-btn sm-btn-secondary sm-btn-sm"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            {t("audit_prev")}
          </button>
          <span className="sm-muted" style={{ fontSize: 13 }}>
            {t("audit_page", { page, totalPages })}
          </span>
          <button
            type="button"
            className="sm-btn sm-btn-secondary sm-btn-sm"
            disabled={page >= totalPages || loading}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            {t("audit_next")}
          </button>
        </div>
      ) : null}
    </div>
  );
}
